import React, { useState, useEffect } from "react";
import {
  Plus,
  Link2,
  ExternalLink,
  Copy,
  Check,
  Edit2,
  Trash2,
  AlertCircle,
  ArrowLeft,
} from "lucide-react";
import Gnavbar from "../../components/Gnavbar";
import {
  getRequest,
  postWithFormData,
  putWithFormData,
  deleteRequest,
} from "../../helpers/api.helper";
import { toast } from "react-toastify";
import { useLanguage } from "../../context/LanguageContext";
import { UPLOAD_URL } from "../../utils/constants";

const CustomizedLinksManagement = () => {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedId, setCopiedId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingLink, setEditingLink] = useState<any>(null);
  const [formData, setFormData] = useState({ title: "", url_link: "" });
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [thumbnailPreview, setThumbnailPreview] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const { t } = useLanguage();

  // 🔹 Fetch links on load
  useEffect(() => {
    fetchLinks();
  }, []);

  const fetchLinks = async () => {
    try {
      setLoading(true);
      const res = await getRequest("/bureau/customized/links");
      if (res.success) {
        setLinks(res.data.links || []);
      } else {
        toast.error(res.message || "Failed to fetch links");
      }
    } catch (error) {
      console.error("Fetch error:", error);
      toast.error("Error fetching links");
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (id: any, url: any) => {
    navigator.clipboard.writeText(url);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const openAddModal = () => {
    setEditingLink(null);
    setFormData({ title: "", url_link: "" });
    setThumbnailFile(null);
    setThumbnailPreview(null);
    setShowModal(true);
  };

  const openEditModal = (link: any) => {
    setEditingLink(link);
    setFormData({ title: link.title, url_link: link.url_link });
    setThumbnailFile(null);
    setThumbnailPreview(
      link.thumbnail
        ? `${UPLOAD_URL}/bureaus/customized_links/${link.thumbnail}`
        : null
    );
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingLink(null);
    setFormData({ title: "", url_link: "" });
    setThumbnailFile(null);
    setThumbnailPreview(null);
  };

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleThumbnailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setThumbnailFile(file);
    if (file) {
      setThumbnailPreview(URL.createObjectURL(file));
    } else {
      setThumbnailPreview(
        editingLink?.thumbnail
          ? `${UPLOAD_URL}/bureaus/customized_links/${editingLink.thumbnail}`
          : null
      );
    }
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();

    if (!formData.title.trim() || !formData.url_link.trim()) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      setSubmitting(true);
      let res;
      const payload = new FormData();
      payload.append("title", formData.title);
      payload.append("url_link", formData.url_link);
      if (thumbnailFile) {
        payload.append("thumbnail", thumbnailFile);
      }
      if (editingLink) {
        // Update existing
        res = await putWithFormData(
          `/bureau/customized/links/${editingLink.id}`,
          payload
        );
      } else {
        // Create new
        res = await postWithFormData("/bureau/customized/links", payload);
      }

      if (res.success) {
        toast.success(
          editingLink
            ? "Link updated successfully!"
            : "Link added successfully!"
        );
        fetchLinks();
        closeModal();
      } else {
        toast.error(res.message || "Failed to save link");
      }
    } catch (error) {
      console.error("Save error:", error);
      toast.error("Error saving link");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: any) => {
    if (!window.confirm("Are you sure you want to delete this link?")) return;
    try {
      const res = await deleteRequest(`/bureau/customized/links/${id}`, {});
      if (res.success) {
        toast.success("Link deleted successfully!");
        fetchLinks();
      } else {
        toast.error(res.message || "Failed to delete link");
      }
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Error deleting link");
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen text-gray-500">
        {t("Loading links")}...
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50">
      <Gnavbar />

      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => window.history.back()}
            className="flex items-center gap-2 text-gray-600 hover:text-purple-600 transition-colors"
          >
            <ArrowLeft size={18} />
            {t("Back")}
          </button>
          <button
            onClick={openAddModal}
            className="flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl"
          >
            <Plus size={20} />
            {t("Add New Link")}
          </button>
        </div>

        {/* Empty State */}
        {links.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-md p-10 text-center border border-gray-100">
            <div className="w-24 h-24 mx-auto mb-4 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center">
              <Link2 size={40} />
            </div>
            <h2 className="text-xl font-semibold mb-2">
              {t("No Links Found")}
            </h2>
            <p className="text-gray-600 mb-6">
              {t(
                "You can add links to share registration forms, YouTube videos, or other business resources."
              )}
            </p>
            <button
              onClick={openAddModal}
              className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-medium hover:from-blue-700 hover:to-purple-700"
            >
              {t("Add Your First Link")}
            </button>
          </div>
        ) : (
          <div className="grid gap-4">
            {links.map((link: any) => (
              <div
                key={link.id}
                className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm hover:shadow-md transition-all flex flex-col sm:flex-row justify-between items-start sm:items-center"
              >
                <div className="flex items-start gap-3 flex-1">
                  {link.thumbnail ? (
                    <img
                      src={`${UPLOAD_URL}/bureaus/customized_links/${link.thumbnail}`}
                      alt={link.title}
                      className="w-10 h-10 rounded-lg object-cover border border-gray-200"
                    />
                  ) : (
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-purple-600 text-white rounded-lg flex items-center justify-center flex-shrink-0">
                      <Link2 size={18} />
                    </div>
                  )}
                  <div className="min-w-0">
                    <h3 className="font-semibold text-gray-900 text-lg truncate">
                      {link.title}
                    </h3>
                    <a
                      href={link.url_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-700 text-sm flex items-center gap-1 break-all"
                    >
                      {link.url_link}
                      <ExternalLink size={14} />
                    </a>
                    <p className="text-xs text-gray-500 mt-1">
                      {t("Added on")}{" "}
                      {new Date(link.created_at).toLocaleDateString("en-IN")}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2 mt-3 sm:mt-0">
                  <button
                    onClick={() => handleCopy(link.id, link.url_link)}
                    className="p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                    title="Copy Link"
                  >
                    {copiedId === link.id ? (
                      <Check size={18} className="text-green-600" />
                    ) : (
                      <Copy size={18} />
                    )}
                  </button>

                  <button
                    onClick={() => openEditModal(link)}
                    className="p-2 text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                    title="Edit"
                  >
                    <Edit2 size={18} />
                  </button>

                  <button
                    onClick={() => handleDelete(link.id)}
                    className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Delete"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ✅ Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-lg overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-5 text-white">
              <h2 className="text-xl font-semibold">
                {editingLink ? "Edit Link" : "Add New Link"}
              </h2>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("Title")} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="e.g., Registration Page"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("URL")} <span className="text-red-500">*</span>
                </label>
                <input
                  type="url"
                  name="url_link"
                  value={formData.url_link}
                  onChange={handleChange}
                  placeholder="https://example.com"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("Thumbnail Image")}
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleThumbnailChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 outline-none"
                />
                {thumbnailPreview && (
                  <img
                    src={thumbnailPreview}
                    alt="Thumbnail preview"
                    className="mt-3 h-20 w-20 rounded-lg object-cover border border-gray-200"
                  />
                )}
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 border border-gray-300 rounded-lg py-2 font-medium text-gray-700 hover:bg-gray-50"
                >
                  {t("Cancel")}
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg py-2 font-medium hover:from-blue-700 hover:to-purple-700 disabled:opacity-50"
                >
                  {submitting
                    ? t("Saving...")
                    : editingLink
                    ? t("Update Link")
                    : t("Add Link")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomizedLinksManagement;
